/**
 * Workspace roster — PURE normalization/merge/copy for the add-people picker
 * (group-threading P2c).
 *
 * The picker used to read `accounts/{accountId}/members` straight from the
 * browser and treat ANY failure as "there is nobody here". That produced the
 * worst possible outcome for a real client: Team & Roles lists two people, the
 * "add people to this thread" dialog says "No one else in this workspace to
 * add", and nothing anywhere explains the contradiction.
 *
 * This module owns the decisions that make that impossible:
 *   - two sources of truth are RECONCILED, not picked between (the server's
 *     `rbacListMembers` — the same call the Team page renders — and the direct
 *     Firestore read of the workspace the thread write path admits against);
 *   - an empty roster is only ever reported as empty when a read actually
 *     SUCCEEDED and returned nothing;
 *   - people who were invited but haven't accepted yet are first-class, because
 *     "I added them to the team, why can't I add them here?" is the question
 *     the old empty state left unanswered.
 *
 * Pure + dependency-free (no firebase, no React) so it unit-tests in isolation,
 * mirroring `addPeopleModel`.
 */
import { readDisplayName } from '../prest0nRuntime/memberDisplayName';
import { isPlatformAgentIdentity } from '../../config/internalAccess';

/** Where a roster entry was seen. Diagnostic — never shown to the user. */
export type RosterSource = 'server' | 'firestore';

export interface NormalizedRosterEntry {
  userId: string;
  displayName?: string;
  /**
   * The invite/login e-mail, or — when the member doc's `email` is blank —
   * the profile's contact e-mail (`profileEmail`, 2026-09-08). A founder's own
   * member doc carried `email: ''` from a seed sweep; the @-mention resolver
   * keys on the e-mail local-part, so "@justinzelaya" resolved to nobody and
   * the picker showed no secondary line. The profile's address is the best
   * identity there is for that row.
   */
  email?: string;
  /** Normalized membership status; absent/blank is treated as 'active' (legacy docs). */
  status: string;
  isOwner: boolean;
  /** True when this member can actually be admitted to a thread in this workspace. */
  active: boolean;
  sources: RosterSource[];
}

export interface PendingInviteEntry {
  inviteId: string;
  email: string;
  displayName?: string;
  /** 'pending' (waiting on the invitee) or 'expired' (link lapsed). */
  status: 'pending' | 'expired';
}

function trim(value: unknown): string {
  return typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim();
}

/**
 * Normalize one raw member doc/record. Tolerant on purpose: member docs are
 * seeded by `rbacAcceptInvitation`, `founderBootstrap`, `addMemberToAccount`,
 * bulk imports and the Firestore console, so identity and status fields drift.
 * Returns null when there is no usable uid — an unidentifiable row must never
 * reach the picker, since the add call keys on the uid.
 */
export function normalizeRosterEntry(
  raw: unknown,
  source: RosterSource,
): NormalizedRosterEntry | null {
  if (!raw || typeof raw !== 'object') return null;
  const rec = raw as Record<string, unknown>;

  // `rbacListMembers` returns `{ id: <docId>, ...data }`; the direct Firestore
  // read normalizes `userId` to the doc id. Accept either.
  const userId = trim(rec.userId) || trim(rec.id) || trim(rec.uid);
  if (!userId) return null;

  const displayName = readDisplayName(rec);
  const email = trim(rec.email) || trim(rec.profileEmail);
  // The platform's own agent identity is the ASSISTANT, not a person to add
  // (2026-09-08: its prest0-inc member doc rendered as an addable "Prest0n
  // (agent)"). Prest0n's row is drawn from the registry fact, never from here.
  if (isPlatformAgentIdentity({ uid: userId, email, memberType: trim(rec.memberType) })) return null;
  const isOwner = rec.isOwner === true;
  // Legacy member docs (addMemberToAccount, hand-seeded rows) carry no `status`.
  // They predate the field and ARE active — the same tolerance the thread write
  // path applies (`parseMemberDoc`), so the picker and the server agree on who
  // is admittable.
  const status = (trim(rec.status) || 'active').toLowerCase();

  return {
    userId,
    ...(displayName ? { displayName } : {}),
    ...(email ? { email } : {}),
    status,
    isOwner,
    active: status === 'active' || isOwner,
    sources: [source],
  };
}

export function normalizeRosterEntries(
  raws: readonly unknown[] | null | undefined,
  source: RosterSource,
): NormalizedRosterEntry[] {
  const out: NormalizedRosterEntry[] = [];
  for (const raw of raws ?? []) {
    const entry = normalizeRosterEntry(raw, source);
    if (entry) out.push(entry);
  }
  return out;
}

/**
 * Union the roster sources, keyed by uid.
 *
 * Union rather than "pick the better source" is deliberate. The two reads can
 * legitimately disagree while an account is mid-migration, and dropping either
 * side re-creates the original defect in the other direction. Fields fill gaps
 * (a name from one source, an email from the other); `active` is OR-ed so a
 * member the write path will admit is never hidden from the person adding them.
 */
export function mergeRosterSources(
  ...groups: ReadonlyArray<readonly NormalizedRosterEntry[]>
): NormalizedRosterEntry[] {
  const byId = new Map<string, NormalizedRosterEntry>();
  for (const group of groups) {
    for (const entry of group ?? []) {
      const existing = byId.get(entry.userId);
      if (!existing) {
        byId.set(entry.userId, { ...entry, sources: [...entry.sources] });
        continue;
      }
      byId.set(entry.userId, {
        ...existing,
        displayName: existing.displayName || entry.displayName,
        email: existing.email || entry.email,
        isOwner: existing.isOwner || entry.isOwner,
        active: existing.active || entry.active,
        status: existing.active ? existing.status : entry.status,
        sources: existing.sources.includes(entry.sources[0])
          ? existing.sources
          : [...existing.sources, ...entry.sources],
      });
    }
  }
  return [...byId.values()];
}

/**
 * Keep only invitations that are still waiting on the invitee. Accepted /
 * cancelled invites are noise — the accepted ones are already in the roster.
 * De-duped by email (a resend + a re-invite are one person to the reader), and
 * anyone already in the roster is dropped so a just-accepted invite can't show
 * up twice while the two reads settle.
 */
export function normalizePendingInvites(
  raws: readonly unknown[] | null | undefined,
  rosterEmails: readonly string[] = [],
): PendingInviteEntry[] {
  const taken = new Set(rosterEmails.map((e) => trim(e).toLowerCase()).filter(Boolean));
  const seen = new Set<string>();
  const out: PendingInviteEntry[] = [];

  for (const raw of raws ?? []) {
    if (!raw || typeof raw !== 'object') continue;
    const rec = raw as Record<string, unknown>;
    const status = trim(rec.status).toLowerCase();
    if (status !== 'pending' && status !== 'expired') continue;

    const email = trim(rec.email);
    if (!email) continue;
    const key = email.toLowerCase();
    if (taken.has(key) || seen.has(key)) continue;
    seen.add(key);

    const displayName = readDisplayName(rec);
    out.push({
      inviteId: trim(rec.inviteId) || trim(rec.id) || key,
      email,
      ...(displayName ? { displayName } : {}),
      status: status === 'expired' ? 'expired' : 'pending',
    });
  }

  return out.sort((a, b) => a.email.toLocaleLowerCase().localeCompare(b.email.toLocaleLowerCase()));
}

/**
 * User-facing copy for a roster read that FAILED. Never used for a read that
 * succeeded and found nobody — that is a different (and honest) empty state.
 */
export function describeRosterFailure(err: unknown): string {
  const code = trim((err as { code?: unknown })?.code).toLowerCase();
  const message = trim((err as { message?: unknown })?.message).toLowerCase();

  if (code.includes('unauthenticated') || message.includes('unauthenticated')) {
    return 'Your session expired — sign in again to see your teammates.';
  }
  if (code.includes('permission-denied') || message.includes('missing or insufficient')) {
    return "You don't have permission to view this workspace's members.";
  }
  if (
    code.includes('unavailable') ||
    code.includes('deadline-exceeded') ||
    message.includes('offline') ||
    message.includes('network')
  ) {
    return "Couldn't reach the server — check your connection and try again.";
  }
  return "Couldn't load your workspace members.";
}
